import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import StepsNav from "components/steps-nav";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GetTrackData } from "services/getTrack";
import {
  IMusicData,
  ITrack,
  ITrackSplit,
  MusicDataKeys
} from "types/musicData";
import StepOne from "./components/steps/step1";
import styled from "styled-components";
import ProgressSteps from "components/progress-steps";
import StepTwo from "./components/steps/step2";
import Spinner from "components/spinner";
import StepThree from "./components/steps/step3";
import StepFour from "./components/steps/step4";
import FinishModal from "./components/finish-modal";
import { ConvertApiResponseToMusicData } from "./components/convertApiResponseToMusicData";
import Track from "./components/steps/track";

const EditMetadata = () => {
  const [musicData, setMusicData] = useState<IMusicData>();
  const [trackId, setTrackId] = useState<string>("");

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const step = searchParams.get("step");
  const type = searchParams.get("type");

  const [finish, setFinish] = useState(false);
  const [activeTrack, setActiveTrack] = useState<number>(0);

  const [currentStep, setCurrentStep] = useState(1);

  const handleNext = () => {
    if (currentStep === 4) {
      setFinish(true);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const GetData = async () => {
    const response = await GetTrackData(id || "", type || "");
    if (response) {
      const { track } = response;

      setTrackId(track.id);

      let musicData = await ConvertApiResponseToMusicData(track);

      setMusicData(musicData);
      setCurrentStep(step ? Number(step) : 1);
    }
  };

  useEffect(() => {
    if (!musicData) {
      GetData();
    }
  }, []);

  const handleChangeStepOne = (key: MusicDataKeys, value: any) => {
    if (musicData) {
      setMusicData((prev) => {
        if (!prev) return prev;
        return { ...prev, [key]: value };
      });
    }
  };
  const updateSplit = (split: ITrackSplit[], index: number) => {
    setMusicData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        trackList: prevData.trackList.map((track, i) =>
          i === index ? { ...track, split: [...split] } : track
        )
      };
    });
  };

  const updateTrack = (index: number, track: ITrack) => {
    setMusicData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        trackList: prevData.trackList.map((t, i) => (i === index ? track : t))
      };
    });
  };

  const handleChangeStepThree = (value: any) => {
    setMusicData((prevData) => {
      if (!prevData) return prevData;
      return { ...prevData, schedule: value };
    });
  };

  const handleChangeStepFour = (value: any) => {
    setMusicData((prevData) => {
      if (!prevData) return prevData;
      return { ...prevData, stores: value };
    });
  };

  const getNavigationState = () => {
    let canNext = false;
    let canPrevious = currentStep > 1;

    if (currentStep === 1) {
      canNext = !!musicData?.coverImage;
    }

    if (currentStep === 2) {
      canNext = true;
    }

    if (currentStep === 3) {
      canNext =
        !!musicData?.schedule.trackPrice &&
        !!musicData?.schedule.proposedReleaseDate;
    }
    if (currentStep === 4 && musicData?.stores) {
      canNext = musicData?.stores?.length > 0;
    }

    return { canNext, canPrevious };
  };

  const { canNext, canPrevious } = useMemo(getNavigationState, [
    currentStep,
    musicData
  ]);

  return (
    <DashboardLayout>
      {musicData ? (
        <>
          <PageLinks
            from={{
              name: musicData.releaseTitle,
              link: `/overview/discography/track/${id}?type=${type}`
            }}
            current="Edit Track"
          />
          <ProgressSteps
            currentStep={currentStep}
            steps={["Track Details", "Splits", "Schedule", "Distribution"]}
          />
          <Body>
            {currentStep === 1 ? (
              <StepOne
                musicData={musicData}
                handleChange={handleChangeStepOne}
                setActiveTrack={(index) => {
                  setActiveTrack(index);
                  setCurrentStep(-1);
                }}
              />
            ) : currentStep === 2 ? (
              <StepTwo
                trackList={musicData.trackList}
                setTrackSplit={(split, index) => updateSplit(split, index)}
              />
            ) : currentStep === 3 ? (
              <StepThree
                data={musicData.schedule}
                setData={handleChangeStepThree}
              />
            ) : currentStep === 4 ? (
              <StepFour
                Stores={musicData.stores}
                setStores={handleChangeStepFour}
              />
            ) : currentStep === -1 ? (
              <Track
                data={musicData.trackList[activeTrack || 0]}
                setTrack={(track) => {
                  updateTrack(activeTrack || 0, track);
                }}
                updateStep={() => {
                  setCurrentStep(2);
                }}
              />
            ) : (
              <></>
            )}
          </Body>

          {finish && (
            <FinishModal
              trackId={trackId}
              finish={finish}
              musicData={musicData}
              close={() => setFinish(false)}
            />
          )}
          {currentStep > 0 && (
            <StepsNav
              steps={4}
              currentStep={currentStep}
              canNext={canNext}
              canPrevious={canPrevious}
              onNext={handleNext}
              onPrevious={handlePrevious}
            />
          )}
        </>
      ) : (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Spinner />
        </div>
      )}
    </DashboardLayout>
  );
};

export default EditMetadata;

const Body = styled.div`
  width: 100%;
  padding-top: 2.4rem;
  padding-bottom: 6.4rem;
  min-height: 67vh;
`;
